import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useQuery } from "react-query";
import { SimpleTable } from "@/v2/components/table/SimpleTable";
import { ColumnDef, CellContext } from "@tanstack/react-table";
import {
  JudgeScoresParsed,
  RegressionTestResultsJudgeRow,
} from "@/v2/entities/regression/Regression.model";
import { Tooltip } from "@/components/shared/tooltip";
import styles from "./Detail.module.scss";

export function AuiRegressionTestDetail() {
  const { manualTestId, auiTestId } = useParams<{
    manualTestId: string;
    auiTestId: string;
  }>();
  const navigate = useNavigate();
  const { getAuiRegressionTestResultsJudge } = useRegressionApi();

  const manualId = manualTestId ?? "";
  const auiId = auiTestId ?? "";

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["aui-regression-tests", "judge-results", manualId, auiId],
    queryFn: () => getAuiRegressionTestResultsJudge(manualId, auiId),
    enabled: manualId.length > 0 && auiId.length > 0,
  });

  const handleBack = useCallback(() => {
    navigate("/automation-center/aui-regression-tests");
  }, [navigate]);

  const setName = rows[0]?.regressionSetName ?? "";
  const firstCreatedAt = rows[0]?.createdAt;

  const verdictCounts = useMemo(() => {
    const quack = rows.filter(
      (r) => r.judgeVerdict?.toLowerCase() === "quack"
    ).length;
    const aui = rows.filter(
      (r) => r.judgeVerdict?.toLowerCase() === "aui"
    ).length;
    const tie = rows.filter(
      (r) => r.judgeVerdict?.toLowerCase() === "tie"
    ).length;
    return { quack, aui, tie, total: rows.length };
  }, [rows]);

  const essenceVerdictCounts = useMemo(() => {
    const quack = rows.filter(
      (r) => r.essenceJudgeVerdict?.toLowerCase() === "quack"
    ).length;
    const aui = rows.filter(
      (r) => r.essenceJudgeVerdict?.toLowerCase() === "aui"
    ).length;
    const tie = rows.filter(
      (r) => r.essenceJudgeVerdict?.toLowerCase() === "tie"
    ).length;
    return { quack, aui, tie, total: rows.length };
  }, [rows]);

  const renderVerdictWithScoresTooltip = useCallback(
    ({ row }: CellContext<RegressionTestResultsJudgeRow, unknown>) => {
      const verdict = row.original.judgeVerdict ?? "";
      const judgeScoresRaw = row.original.judgeScores;
      let parsed: JudgeScoresParsed | null = null;
      if (judgeScoresRaw) {
        try {
          parsed = JSON.parse(judgeScoresRaw) as JudgeScoresParsed;
        } catch {
          parsed = null;
        }
      }
      return (
        <Tooltip>
          <Tooltip.Trigger>
            <Text
              size="1"
              className={parsed ? styles.verdictTooltipTrigger : undefined}
            >
              {verdict}
            </Text>
          </Tooltip.Trigger>
          {parsed ? (
            <Tooltip.Content side="left" sideOffset={8}>
              <Flex
                direction="column"
                gap="2"
                className={styles.verdictTooltipContent}
              >
                <Text size="1" weight="bold">
                  Quack
                </Text>
                <Flex direction="column" gap="1">
                  <Text size="1">
                    Factual accuracy: {parsed.quack_scores.factual_accuracy} ·
                    Content completeness:{" "}
                    {parsed.quack_scores.content_completeness} · Primary intent:{" "}
                    {parsed.quack_scores.primary_intent_coverage}
                  </Text>
                  <Text size="1">
                    Irrelevant content: {parsed.quack_scores.irrelevant_content} ·
                    Persona & tone: {parsed.quack_scores.persona_tone} · Format:{" "}
                    {parsed.quack_scores.format_structure}
                  </Text>
                  <Text size="1" weight="medium">
                    Total: {parsed.quack_scores.total}
                  </Text>
                </Flex>
                <Text size="1" weight="bold">
                  AUI
                </Text>
                <Flex direction="column" gap="1">
                  <Text size="1">
                    Factual accuracy: {parsed.aui_scores.factual_accuracy} ·
                    Content completeness: {parsed.aui_scores.content_completeness}{" "}
                    · Primary intent: {parsed.aui_scores.primary_intent_coverage}
                  </Text>
                  <Text size="1">
                    Irrelevant content: {parsed.aui_scores.irrelevant_content} ·
                    Persona & tone: {parsed.aui_scores.persona_tone} · Format:{" "}
                    {parsed.aui_scores.format_structure}
                  </Text>
                  <Text size="1" weight="medium">
                    Total: {parsed.aui_scores.total}
                  </Text>
                </Flex>
              </Flex>
            </Tooltip.Content>
          ) : null}
        </Tooltip>
      );
    },
    []
  );

  const columns = useMemo<ColumnDef<RegressionTestResultsJudgeRow>[]>(
    () => [
      {
        id: "regressionSetItemQuestion",
        accessorKey: "regressionSetItemQuestion",
        header: "Question",
      },
      {
        id: "regressionSetItemExpectedAnswer",
        accessorKey: "regressionSetItemExpectedAnswer",
        header: "Expected answer",
      },
      {
        id: "manualRegressionTestResultAiResponse",
        accessorKey: "manualRegressionTestResultAiResponse",
        header: "Quack's answer",
      },
      {
        id: "auiRegressionTestResultAiResponse",
        accessorKey: "auiRegressionTestResultAiResponse",
        header: "AUI answer",
      },
      {
        id: "manualRegressionTestResultDecision",
        accessorKey: "manualRegressionTestResultDecision",
        header: "Quack's Decision",
      },
      {
        id: "auiRegressionTestResultDecision",
        accessorKey: "auiRegressionTestResultDecision",
        header: "AUI Decision",
      },
      {
        id: "judgeVerdict",
        accessorKey: "judgeVerdict",
        header: "Verdict",
        cell: renderVerdictWithScoresTooltip,
        meta: { cellTooltip: false },
      },
      {
        id: "judgeReasoning",
        accessorKey: "judgeReasoning",
        header: "Reasoning",
      },
      {
        id: "manualRegressionTestResultAiResponseEssence",
        accessorKey: "manualRegressionTestResultAiResponseEssence",
        header: "Essence quack",
      },
      {
        id: "auiRegressionTestResultAiResponseEssence",
        accessorKey: "auiRegressionTestResultAiResponseEssence",
        header: "Essence AUI",
      },
      {
        id: "essenceJudgeVerdict",
        accessorKey: "essenceJudgeVerdict",
        header: "Verdict judge essence winner (quack/aui/tie)",
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="6" px="6" py="6">
      <Flex direction="row" gap="2" justify="between">
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Button variant="ghost" size="1" onClick={handleBack}>
              <ArrowLeftIcon />
            </Button>
            <Text size="5" weight="medium">
              Judge Results: Manual / AUI Pair
            </Text>
          </Flex>
          {setName ? (
            <Text size="2" weight="medium">
              {setName}
            </Text>
          ) : null}
          <Text size="2" color="gray">
            Quack: ...{manualTestId?.slice(-8)} · AUI: ...
            {auiTestId?.slice(-8)}
          </Text>
          {firstCreatedAt ? (
            <Text size="2" color="gray">
              Created: {new Date(firstCreatedAt).toLocaleString()}
            </Text>
          ) : null}
          <Text size="2" color="gray">
            Quack: {verdictCounts.quack} · AUI: {verdictCounts.aui} · Tie:{" "}
            {verdictCounts.tie} · Total: {verdictCounts.total}
          </Text>
          <Text size="2" color="gray">
            Essence: Quack: {essenceVerdictCounts.quack} · AUI:{" "}
            {essenceVerdictCounts.aui} · Tie: {essenceVerdictCounts.tie} ·
            Total: {essenceVerdictCounts.total}
          </Text>
        </Flex>
      </Flex>

      <SimpleTable
        data={rows}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
      />
    </Flex>
  );
}
